import { useRef, useState } from "react";
import { api } from "../api";
import { ErrorNote } from "../components/Feedback";
import { md5OfFile, uploadErrorText } from "../components/UploadPanel";
import { Btn } from "../components/Shared";
import { SuccessNote } from "./admin/parts";
import { CreateFieldsBox } from "./StudyCrateImport";

// "Import RO-Crate" on the project page (a curator of the project, while it has no studies):
// a project crate zip, as "Download RO-Crate" writes it, becomes this project's studies with
// their datasets, files (a protocol is one of them), dictionaries and analyses
// (backend/unio/routers/crate_import.py, task 2026-10-01-ro-crates-import-into-a-project-...).
// Everything imported is authored by the curator who imports it (N4a): nothing to map.
//
//   POST /projects/{id}/import/ro-crate[?create_fields=true]   raw body
//   -> {kind: "project", studies, datasets, analyses, missing, skipped, warnings, fields_created}
// "Create the fields this project lacks" (task F): the crate's field keys the project has no
// field for become fields of this project first, so their values are imported.

function Listed({ label, items, render }) {
  if (!items.length) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <h4 style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 700 }}>{label}</h4>
      <ul aria-label={label} style={{ margin: 0, paddingLeft: 20 }}>
        {items.map((x, i) => <li key={i}>{render(x)}</li>)}
      </ul>
    </div>
  );
}

const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function ImportResult({ result }) {
  const files = result.datasets.reduce((sum, d) => sum + d.file_count, 0);
  return (
    <section aria-label="Project RO-Crate import result" style={{ marginTop: 12, fontSize: 14 }}>
      <SuccessNote>
        Imported {count(result.studies.length, "study", "studies")},{" "}
        {count(result.datasets.length, "dataset", "datasets")} with {count(files, "file", "files")} and{" "}
        {count(result.analyses.length, "analysis", "analyses")}.
      </SuccessNote>
      <Listed label="Fields created" items={result.fields_created || []} render={f => f} />
      <Listed label="Missing files" items={result.missing} render={m => `${m.name}: ${m.reason}`} />
      <Listed label="Warnings" items={result.warnings} render={w => w} />
      <Listed label="Not imported" items={result.skipped} render={s => `${s.name}: ${s.reason}`} />
    </section>
  );
}

export default function ProjectCrateImport({ project, onImported }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [createFields, setCreateFields] = useState(false);
  const input = useRef(null);

  const choose = async e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setResult(null);
    if (file.size === 0) {
      setError(new Error(`${file.name} is empty (0 bytes).`));
      return;
    }
    setBusy(true);
    try {
      const md5 = await md5OfFile(file);
      const q = createFields ? "?create_fields=true" : "";
      setResult(await api.upload(`/projects/${project.id}/import/ro-crate${q}`, file, { md5 }));
      onImported();
    } catch (err) {
      setError(new Error(uploadErrorText(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 6 }}>
      {/* Not a tab stop of its own: the button below opens it (as UploadPanel's). Hidden with
          display: none, not a 1 px box, which the phone rules would grow into a 44 px row. */}
      <input ref={input} id={`project-crate-import-${project.id}`} type="file" accept=".zip,application/zip"
             aria-label="Import RO-Crate" style={{ display: "none" }} onChange={choose} disabled={busy} />
      <Btn variant="secondary" onClick={() => input.current && input.current.click()} disabled={busy}>
        {busy ? "Importing…" : "Import RO-Crate"}
      </Btn>
      <CreateFieldsBox checked={createFields} onChange={setCreateFields} disabled={busy} />
      <ErrorNote error={error} />
      {result && <ImportResult result={result} />}
    </span>
  );
}
